/* 曹彰（字子文） */
import { SG, gd, spMul } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, S, D } from '../define.ts';

export default defineHero({
  id: 'caozhang', name: '曹彰', sub: '字子文', faction: 'wei', role: '射手', weapon: 'bow',
  look: { weaponName: '鐵胎弓', beard: 'bushy', hair: '#c9a24e', bulk: 1.08, fx: '#ffcf5a', c: { plume: '#e9c46a', cape: '#8a6a2a' } },
  skills: [
    ['鐵弓', 'bolt', '朝方向射出重箭，命中第一個敵人造成 {90/140/190/240} 傷害並擊退。', { x: { dmg: [90, 140, 190, 240], cc: { knockback: 80 } }, aim: D(800, 20), range: 800, cd: 9, mp: [60, 70, 80, 90], el: null }],
    ['猛獸搏', 'passive', '普攻命中武將時疊「勇」，每層攻速 +{4/6/8/10}%，最多 5 層，4 秒沒疊就歸零。', { pv: {}, sig: 'brave', as: [4, 6, 8, 10], stack: 'braveN', max: 5 }],
    ['黃鬚騎射', 'buff', '開關技：上馬後可以邊移動邊普攻（不必停下來），移速 +8%，但移動中的普攻傷害 -{25/20/15/10}%。上馬耗 40 氣，騎馬時每秒耗最大氣的 6%，沒氣就下馬。', { x: { k: 'mount', pen: [25, 20, 15, 10], cost: 40, drainPct: 6 }, aim: S(0), cd: 1, mp: 0, el: 'wind' }],
    ['北征烏桓', 'volley', '3 秒內朝游標方向連射 {20/25/30} 箭，每箭 {30/38/46} 傷害；射擊時可以自由移動，方向跟著游標走。', { x: { k: 'barrage', n: [20, 25, 30], dmg: [30, 38, 46], dur: 3 }, aim: D(800, 30), cd: [80, 70, 60], mp: [150, 175, 200], el: null, shout: '黃鬚兒在此！' }],
  ],
  // 專屬技能（技能資料裡的 x.k）
  kinds: {
    mount(u, a, X) {
      if (u.mounted) { this.dismount(u); return; }
      if (u.mp < X.cost) { this.tell(u, `黃鬚騎射：上馬需要 ${X.cost} 氣`, 'warn'); return; }
      u.mp -= X.cost;
      u.mounted = { pen: X.pen, drain: X.drainPct / 100 }; // 每秒耗最大氣的比例：前後期都只能騎差不多久
      this.addBuff(u, { id: 'mount', t: 1e6, ms: 0.08 });
      this.tell(u, '黃鬚騎射：上馬，可以邊走邊射', 'info');
    },
    barrage(u, a, X) {
      u.barrage = { until: this.t + X.dur, next: this.t, gap: X.dur / X.n, dmg: X.dmg * spMul(u) };
    },
  },
  methods: {
    dismount(u) { if (!u.mounted) return; u.mounted = null; this.addBuff(u, { id: 'mount', t: 0.01 }); this.tell(u, '黃鬚騎射：下馬', 'info'); },
  },
  // 被動與專屬效果：hook(時間點, 順位, 處理)（時間點一覽見 core/hooks.ts）
  hooks: [
    // 黃鬚騎射：移動中的普攻打折
    hook('attack', 50, function (u, a, next) {
      if (a.moving && u.mounted) { const atk = u.atk; u.atk = atk * (1 - u.mounted.pen / 100); try { next(); } finally { u.atk = atk; } return; }
      return next();
    }),
    // 猛獸搏：普攻打中武將疊攻速
    hook('onHit', 20, function (u, t) {
      const br = u.sg && u.sg.brave;
      if (br && t.kind === 'hero') { u.braveN = Math.min(5, (u.braveN || 0) + 1); u.braveT = this.t + 4; this.addBuff(u, { id: 'brave', t: 4, as: u.braveN * br.v('as') }); }
    }),
    // 猛獸搏：4 秒沒疊就歸零
    hook('tick', 60, function (u, dt) { if (u.braveN && u.braveT < this.t) u.braveN = 0; }),
    // 黃鬚騎射：耗氣、邊走邊射
    hook('tick', 100, function (u, dt) {
      if (u.mounted) {
        u.mp -= u.mounted.drain * u.maxMp * dt;
        if (u.mp <= 0) { u.mp = 0; this.dismount(u); }
        else {
          const moved = u.lastPos2 ? Math.hypot(u.x - u.lastPos2[0], u.y - u.lastPos2[1]) : 0;
          if (moved > 0.5 && !u.act && u.atkCd <= 0 && u.st.disarm <= 0 && u.st.stun <= 0) {
            const I = u.input, inR = (t: Unit) => t && t.alive && this.isEnemy(u, t) && this.inRange(u, t);
            let t = inR(I.target) ? I.target : null;
            if (!t) { let bd = 1e9; for (const o of this.units) if (inR(o) && !o.structure && o.team < 2) { const d = gd(u.x, u.y, o.x, o.y) - (o.kind === 'hero' ? 200 : 0); if (d < bd) { bd = d; t = o; } } }
            if (t) {
              u.atkCd = Math.max(0.3, 1.15 * 100 / SG.effAsp(u));
              this.attackHit(u, { target: t, dir: SG.dirTo(u, t.x, t.y), moving: true });
            }
          }
        }
      }
      u.lastPos2 = [u.x, u.y];
    }),
    // 北征烏桓：連射
    hook('tick', 110, function (u, dt) {
      const b = u.barrage;
      if (b) {
        if (b.until <= this.t || !u.alive) u.barrage = null;
        else while (b.next <= this.t) {
          b.next += b.gap;
          if (u.st.stun > 0 || u.st.freeze > 0 || u.st.knockup > 0) continue;
          const I = u.input, tg = u.ai && u.ai.target;
          const aim = u.human ? I.aim : tg && tg.alive ? [tg.x, tg.y] : null;
          let dir = aim ? SG.dirTo(u, aim[0], aim[1]) : [u.f, 0];
          const j = (SG.rand() - 0.5) * 0.12, c = Math.cos(j), sn = Math.sin(j);
          dir = [dir[0] * c - dir[1] * sn, dir[0] * sn + dir[1] * c];
          if (Math.abs(dir[0]) > 0.05) u.f = Math.sign(dir[0]);
          this.proj(u, { dir, speed: 1150, range: 820, dmg: b.dmg, shape: 'arrow', col: u.spec.fx, visual: true, rad: 14 });
        }
      }
    }),
    hook('revive', 30, function (u) { u.mounted = null; u.barrage = null; }),
  ],
  // 電腦什麼時候放這招
  ai: {
    mount: ({ w, u, t, d, hero, hpR, foeHeroes }) => !u.mounted && !!t && u.mp > u.maxMp * 0.4,
    barrage: ({ w, u, t, d, hero, hpR, foeHeroes }) => hero && d < 750,
  },
});
